import { MetaLayout } from "@/common/MetaLayout";
import LoadMore from "@/components/LoadMore";
import PaperCard from "@/components/PaperCard";
import ErrorPage from "@/pages/_error";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";
import { Environment, graphql, usePaginationFragment } from "react-relay";
import { Link } from "react-router";
import { EntityPapersPageFragment$key } from "./__generated__/EntityPapersPageFragment.graphql";
import { EntityPapersPageQuery as EntityPapersPageQueryType } from "./__generated__/EntityPapersPageQuery.graphql";

const Fragment = graphql`
  fragment EntityPapersPageFragment on User
  @refetchable(queryName: "EntityPapersPageFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    papers(first: $count, after: $cursor)
      @connection(key: "EntityPapersPageFragment_papers") {
      edges {
        node {
          id
          ...PaperCardFragment
        }
      }
    }
  }
`;

export default function EntityPapersPage() {
  const intl = useIntl();
  const {
    query: { entityByUsername, viewer },
    variables,
  } = useEntityPapersPageQuery();
  // Only users have papers; an organization's page is not found.
  const user =
    entityByUsername?.__typename === "User" ? entityByUsername : null;
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    Fragment,
    user as EntityPapersPageFragment$key | null,
  );
  if (!entityByUsername || !data) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  const isViewer = viewer?.id === entityByUsername.id;
  if (!data.papers.edges.length) {
    return (
      <p className="text-grey-500">
        {isViewer ? (
          <FormattedMessage
            defaultMessage="No papers are linked to you yet. Papers that list your verified email or your ORCID iD on OpenAlex, or that a publisher added to your ORCID record, show here. You can verify your email and link your iD from your <link>profile settings</link>."
            values={{
              link: (chunks) => (
                <Link
                  to={`/${entityByUsername.username}/edit`}
                  className="text-brand-500 hover:underline"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        ) : (
          <FormattedMessage defaultMessage="No papers are linked to this profile yet." />
        )}
      </p>
    );
  }
  return (
    <MetaLayout
      hideTitleTemplate
      metaTitle={
        isViewer
          ? intl.formatMessage({ defaultMessage: "My Papers" })
          : undefined
      }
    >
      <div className="grid xl:grid-cols-2 grid-cols-1 gap-6">
        {data.papers.edges.map(({ node }) => (
          <PaperCard paper={node} key={node.id} />
        ))}
        <LoadMore
          loadMore={loadNext}
          hasMore={hasNext}
          isLoading={isLoadingNext}
        />
      </div>
    </MetaLayout>
  );
}

const EntityPapersPageQuery = graphql`
  query EntityPapersPageQuery($username: String!) {
    viewer @ifAllowed {
      id
    }
    entityByUsername(username: $username) {
      __typename
      id
      username
      ... on User {
        ...EntityPapersPageFragment
      }
    }
  }
`;

export function loader(environment: Environment) {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<EntityPapersPageQueryType>(
      environment,
      EntityPapersPageQuery,
      { username },
    );
  };
}

const useEntityPapersPageQuery = () =>
  usePreloaded<EntityPapersPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Papers" }),
};
